import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { ContentData, DailyDoc, ReferenceDoc, SqlFile } from '@/types'
import {
  getDailyDocs,
  getReferenceDocs,
  getSqlFiles,
  type FileMetadata,
  type FileChangeMessage
} from '@/api/contentApi'

export const useContentStore = defineStore('content', () => {
  // State
  const contentData = ref<ContentData | null>(null)
  const isLoading = ref(true)
  const error = ref<string | null>(null)

  // Getters
  const dailyDocs = computed(() => contentData.value?.dailyDocs || [])
  const referenceDocs = computed(() => contentData.value?.referenceDocs || [])
  const sqlFiles = computed(() => contentData.value?.sqlFiles || [])
  const metadata = computed(() => contentData.value?.metadata)

  const todaysDoc = computed(() => {
    // Use local date, not UTC (toISOString gives UTC which can be off by a day)
    const today = new Date()
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, '0')
    const day = String(today.getDate()).padStart(2, '0')
    const dateString = `${year}-${month}-${day}`
    return dailyDocs.value.find(doc => doc.date === dateString)
  })

  const topTags = computed(() => {
    if (!metadata.value) return []
    const tagCounts = metadata.value.tagCounts
    return Object.entries(tagCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([tag, count]) => ({ tag, count }))
  })

  const recentFiles = computed(() => {
    const allFiles: Array<{
      type: 'daily' | 'reference' | 'sql'
      item: DailyDoc | ReferenceDoc | SqlFile
    }> = [
      ...dailyDocs.value.map(doc => ({ type: 'daily' as const, item: doc })),
      ...referenceDocs.value.map(doc => ({ type: 'reference' as const, item: doc })),
      ...sqlFiles.value.map(file => ({ type: 'sql' as const, item: file }))
    ]

    return allFiles
      .sort((a, b) => {
        const aTime = new Date(a.item.modifiedAt).getTime()
        const bTime = new Date(b.item.modifiedAt).getTime()
        return bTime - aTime
      })
      .slice(0, 10)
      .map(({ type, item }) => ({
        type,
        title: 'title' in item ? item.title : (item as SqlFile).filename,
        path:
          type === 'daily'
            ? (item as DailyDoc).date
            : type === 'reference'
              ? (item as ReferenceDoc).path
              : (item as SqlFile).filename,
        modifiedAt: item.modifiedAt,
        relativeTime: getRelativeTime(new Date(item.modifiedAt))
      }))
  })

  // Helper functions to map backend FileMetadata to frontend types
  function mapToDailyDoc(metadata: FileMetadata): DailyDoc {
    // Extract date from path (e.g., "daily/2025-10/2025-10-25-saturday.md")
    const dateMatch = metadata.path.match(/(\d{4}-\d{2}-\d{2})/)
    const date = dateMatch ? dateMatch[1] : ''

    // Extract day name from filename
    const dayNameMatch = metadata.name.match(/-([a-z]+)\.md$/)
    const dayName = dayNameMatch ? dayNameMatch[1] : ''

    return {
      date,
      dayName,
      title: metadata.title || metadata.name,
      content: '', // Content loaded on demand
      tags: metadata.tags || [],
      wordCount: metadata.wordCount || 0,
      filePath: metadata.path,
      modifiedAt: metadata.lastModified
    }
  }

  function mapToReferenceDoc(metadata: FileMetadata): ReferenceDoc {
    // Extract category from path (e.g., "reference/java/fundamentals.md" -> "java")
    const categoryMatch = metadata.path.match(/^reference\/([^/]+)/)
    const category = categoryMatch ? categoryMatch[1] : ''

    // Path relative to reference/ directory
    const relativePath = metadata.path.replace(/^reference\//, '')

    return {
      path: relativePath,
      category,
      title: metadata.title || metadata.name,
      content: '', // Content loaded on demand
      tags: metadata.tags || [],
      wordCount: metadata.wordCount || 0,
      filePath: metadata.path,
      modifiedAt: metadata.lastModified
    }
  }

  function mapToSqlFile(metadata: FileMetadata): SqlFile {
    // Extract title from filename (e.g., "user-lookups" from "user-lookups.sql")
    const title = metadata.name.replace(/\.sql$/, '').replace(/-/g, ' ')

    return {
      filename: metadata.name,
      title: metadata.title || title,
      description: metadata.preview || '',
      content: '', // Content loaded on demand
      filePath: metadata.path,
      modifiedAt: metadata.lastModified,
      lineCount: 0 // Not provided by backend metadata
    }
  }

  // Actions
  async function loadContent() {
    isLoading.value = true
    error.value = null

    try {
      // Fetch data from backend API
      const [dailyMetadata, referenceMetadata, sqlMetadata] = await Promise.all([
        getDailyDocs(),
        getReferenceDocs(),
        getSqlFiles()
      ])

      // Map backend metadata to frontend types
      const dailyDocsData = dailyMetadata.map(mapToDailyDoc)
      const referenceDocsData = referenceMetadata.map(mapToReferenceDoc)
      const sqlFilesData = sqlMetadata.map(mapToSqlFile)

      // Calculate metadata
      const allTags = new Set<string>()
      const tagCounts: Record<string, number> = {}
      let totalWords = 0

      ;[...dailyDocsData, ...referenceDocsData].forEach(doc => {
        doc.tags.forEach(tag => {
          allTags.add(tag)
          tagCounts[tag] = (tagCounts[tag] || 0) + 1
        })
        totalWords += doc.wordCount
      })

      contentData.value = {
        metadata: {
          generatedAt: new Date().toISOString(),
          totalDailyDocs: dailyDocsData.length,
          totalReferenceDocs: referenceDocsData.length,
          totalSqlFiles: sqlFilesData.length,
          totalWords,
          allTags: Array.from(allTags),
          tagCounts
        },
        dailyDocs: dailyDocsData,
        referenceDocs: referenceDocsData,
        sqlFiles: sqlFilesData
      }

      isLoading.value = false
    } catch (err) {
      error.value = 'Failed to load content from backend'
      console.error('Error loading content:', err)
      isLoading.value = false
    }
  }

  function getDailyDocByDate(date: string): DailyDoc | undefined {
    return dailyDocs.value.find(doc => doc.date === date)
  }

  function getReferenceDocByPath(path: string): ReferenceDoc | undefined {
    return referenceDocs.value.find(doc => doc.path === path)
  }

  function getSqlFileByFilename(filename: string): SqlFile | undefined {
    return sqlFiles.value.find(
      file => file.filename === filename || file.filename === `${filename}.sql`
    )
  }

  /**
   * Handle file change notification from WebSocket
   */
  function handleFileChange(message: FileChangeMessage) {
    if (!contentData.value) return

    console.log('[ContentStore] File change received:', message.changeType, message.path)

    const { changeType, path, metadata } = message

    // Determine file type from path
    if (path.startsWith('daily/')) {
      handleDailyDocChange(changeType, path, metadata)
    } else if (path.startsWith('reference/')) {
      handleReferenceDocChange(changeType, path, metadata)
    } else if (path.startsWith('sql/')) {
      handleSqlFileChange(changeType, path, metadata)
    }
  }

  function handleDailyDocChange(
    changeType: FileChangeMessage['changeType'],
    path: string,
    metadata?: FileMetadata
  ) {
    if (!contentData.value) return

    const dailyDocsArray = contentData.value.dailyDocs

    if (changeType === 'DELETED') {
      // Remove the document
      const index = dailyDocsArray.findIndex(doc => doc.filePath === path)
      if (index !== -1) {
        dailyDocsArray.splice(index, 1)
        contentData.value.metadata.totalDailyDocs--
      }
    } else if (changeType === 'CREATED' && metadata) {
      // Add new document
      const newDoc = mapToDailyDoc(metadata)
      dailyDocsArray.push(newDoc)
      contentData.value.metadata.totalDailyDocs++
    } else if (changeType === 'MODIFIED' && metadata) {
      // Update existing document
      const index = dailyDocsArray.findIndex(doc => doc.filePath === path)
      if (index !== -1) {
        dailyDocsArray[index] = mapToDailyDoc(metadata)
      }
    }
  }

  function handleReferenceDocChange(
    changeType: FileChangeMessage['changeType'],
    path: string,
    metadata?: FileMetadata
  ) {
    if (!contentData.value) return

    const referenceDocsArray = contentData.value.referenceDocs

    if (changeType === 'DELETED') {
      const index = referenceDocsArray.findIndex(doc => doc.filePath === path)
      if (index !== -1) {
        referenceDocsArray.splice(index, 1)
        contentData.value.metadata.totalReferenceDocs--
      }
    } else if (changeType === 'CREATED' && metadata) {
      const newDoc = mapToReferenceDoc(metadata)
      referenceDocsArray.push(newDoc)
      contentData.value.metadata.totalReferenceDocs++
    } else if (changeType === 'MODIFIED' && metadata) {
      const index = referenceDocsArray.findIndex(doc => doc.filePath === path)
      if (index !== -1) {
        referenceDocsArray[index] = mapToReferenceDoc(metadata)
      }
    }
  }

  function handleSqlFileChange(
    changeType: FileChangeMessage['changeType'],
    path: string,
    metadata?: FileMetadata
  ) {
    if (!contentData.value) return

    const sqlFilesArray = contentData.value.sqlFiles

    if (changeType === 'DELETED') {
      const index = sqlFilesArray.findIndex(file => file.filePath === path)
      if (index !== -1) {
        sqlFilesArray.splice(index, 1)
        contentData.value.metadata.totalSqlFiles--
      }
    } else if (changeType === 'CREATED' && metadata) {
      const newFile = mapToSqlFile(metadata)
      sqlFilesArray.push(newFile)
      contentData.value.metadata.totalSqlFiles++
    } else if (changeType === 'MODIFIED' && metadata) {
      const index = sqlFilesArray.findIndex(file => file.filePath === path)
      if (index !== -1) {
        sqlFilesArray[index] = mapToSqlFile(metadata)
      }
    }
  }

  // Utility function for relative time
  function getRelativeTime(date: Date): string {
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const seconds = Math.floor(diff / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`
    return 'just now'
  }

  return {
    // State
    contentData,
    isLoading,
    error,
    // Getters
    dailyDocs,
    referenceDocs,
    sqlFiles,
    metadata,
    todaysDoc,
    topTags,
    recentFiles,
    // Actions
    loadContent,
    getDailyDocByDate,
    getReferenceDocByPath,
    getSqlFileByFilename,
    handleFileChange
  }
})
